import { useState } from 'react';
import { App } from 'antd';
import FileViewerLazy from '../components/FileViewerLazy';
import * as earnApi from '../api/earnings';

/**
 * Opens a payout's payment voucher in the in-app viewer — shared by the payout
 * sheet/drawer and both statement screens, so a voucher opens the same way
 * wherever its number is shown.
 *
 * The link is signed for ten minutes only, so it is asked for on tap rather than
 * carried on the list row: a row read half an hour ago would hand the viewer a
 * dead link.
 *
 * The viewer sits at z-index 4500 and the phone's bottom sheets at 4600, so it
 * is lifted into a layer above them: a voucher opened FROM the payout sheet has
 * to cover it, and closing the viewer lands the agent back on that sheet. Still
 * below antd's popups (6000), so an error toast shows over it.
 *
 *   const pv = useVoucherViewer();
 *   <Button loading={pv.busy === id} onClick={() => pv.open(id)} />
 *   pv.open(key, () => fetchOtherFile())   // any other file, same viewer
 *   {pv.viewer}
 */
export default function useVoucherViewer() {
  const { message } = App.useApp();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(null); // payout id whose link is being fetched

  // load: another file to fetch instead of the payout's voucher (an ERP213
  // voucher's scan); it must resolve to { url, file_name, doc_no }.
  const open = async (payoutId, load) => {
    if (!payoutId || busy) return;
    setBusy(payoutId);
    try {
      const r = await (load ? load() : earnApi.getEarningsPayoutVoucher(payoutId));
      setFile(r?.data ?? r);
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const viewer = file?.url ? (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4700 }}>
      <FileViewerLazy
        title={`Payment voucher ${file.doc_no || ''}${file.voided ? ' · voided' : ''}`.trim()}
        url={file.url}
        fileName={file.file_name}
        onClose={() => setFile(null)}
      />
    </div>
  ) : null;

  return { open, busy, viewer };
}
